(() => {
  const config = window.scantrackTheme || {};
  const routes = config.routes || {};
  const strings = config.strings || {};
  const root = routes.root || '/';
  const endpoint = (path) => `${root.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || config.currency || 'EUR'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${config.currency || 'EUR'}`;
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-backdrop]');
      this.body = document.querySelector('[data-cart-drawer-body]');
      this.footer = document.querySelector('[data-cart-drawer-footer]');
      this.status = document.querySelector('[data-cart-status]');
      this.lastFocused = null;
      if (!this.drawer) return;
      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        const closer = event.target.closest('[data-cart-close]');
        const quantityButton = event.target.closest('[data-cart-quantity]');
        const removeButton = event.target.closest('[data-cart-remove]');

        if (opener) {
          event.preventDefault();
          this.open();
        }
        if (closer) {
          event.preventDefault();
          this.close();
        }
        if (quantityButton) {
          event.preventDefault();
          const line = Number(quantityButton.dataset.line);
          const quantity = Number(quantityButton.dataset.quantity);
          this.change(line, quantity);
        }
        if (removeButton) {
          event.preventDefault();
          this.change(Number(removeButton.dataset.line), 0);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-product-form]');
        if (!form) return;
        event.preventDefault();
        this.add(form);
      });
    }

    async request(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...options.headers },
        ...options
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || strings.error || 'Request failed');
      return data;
    }

    async getCart() {
      return this.request(endpoint('/cart.js'));
    }

    async add(form) {
      const submit = form.querySelector('[type="submit"]');
      const message = form.querySelector('[data-product-form-message]');
      if (submit) submit.disabled = true;
      if (message) message.textContent = '';
      this.setStatus('');

      try {
        const formData = new FormData(form);
        await this.request(routes.cartAdd || endpoint('/cart/add.js'), { method: 'POST', body: formData });
        const cart = await this.getCart();
        this.render(cart);
        this.open();
      } catch (error) {
        if (message) message.textContent = error.message;
        this.setStatus(error.message);
      } finally {
        if (submit) submit.disabled = false;
      }
    }

    async change(line, quantity) {
      this.setStatus('');
      try {
        const cart = await this.request(routes.cartChange || endpoint('/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        this.render(cart);
      } catch (error) {
        this.setStatus(error.message);
      }
    }

    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });

      if (!cart.items.length) {
        this.body.innerHTML = `<div class="empty-state">${escapeHtml(strings.cartEmpty || 'Your cart is empty')}</div>`;
        this.footer.hidden = true;
        return;
      }

      this.body.innerHTML = cart.items.map((item, index) => {
        const image = item.image ? `<img class="cart-drawer__line-image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="128" height="128" loading="lazy">` : '<div class="cart-drawer__line-image"></div>';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer__line-price">${escapeHtml(item.variant_title)}</div>` : '';
        return `
          <div class="cart-drawer__line">
            ${image}
            <div>
              <a href="${escapeHtml(item.url)}" class="cart-drawer__line-title">${escapeHtml(item.product_title)}</a>
              ${variant}
              <div class="cart-drawer__line-price">${formatMoney(item.final_line_price)}</div>
              <div class="quantity-control" aria-label="Quantity">
                <button type="button" data-cart-quantity data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-quantity data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <button type="button" class="cart-drawer__remove" data-cart-remove data-line="${index + 1}" aria-label="Remove">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18"></path></svg>
            </button>
          </div>`;
      }).join('');

      const total = this.footer.querySelector('[data-cart-total]');
      if (total) total.textContent = formatMoney(cart.total_price);
      this.footer.hidden = false;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    open() {
      if (!this.drawer) return;
      this.lastFocused = document.activeElement;
      this.drawer.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      this.drawer.querySelector('[data-cart-close]')?.focus();
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.backdrop?.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      this.lastFocused?.focus?.();
    }
  }

  const initializeProductForms = () => {
    document.querySelectorAll('[data-product-root]').forEach((rootElement) => {
      if (rootElement.dataset.initialized === 'true') return;
      rootElement.dataset.initialized = 'true';
      const select = rootElement.querySelector('[data-variant-select]');
      const price = rootElement.querySelector('[data-product-price]');
      const compare = rootElement.querySelector('[data-product-compare-price]');
      const button = rootElement.querySelector('[data-add-to-cart-button]');
      const buttonText = rootElement.querySelector('[data-add-to-cart-text]');
      const image = rootElement.querySelector('[data-product-main-image], .featured-product__media img, .scantrack-hero__media img, .product-gallery__main img');

      select?.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const available = option.dataset.available === 'true';
        if (price) price.textContent = formatMoney(option.dataset.price);
        if (compare) {
          const comparePrice = Number(option.dataset.comparePrice || 0);
          compare.textContent = comparePrice > Number(option.dataset.price || 0) ? formatMoney(comparePrice) : '';
          compare.hidden = !compare.textContent;
        }
        if (button) button.disabled = !available;
        if (buttonText) {
          const customLabel = button?.dataset.label || strings.addToCart || 'Add to cart';
          const labelWithPrice = button?.dataset.showPrice === 'true' ? `${customLabel} — ${formatMoney(option.dataset.price)}` : customLabel;
          buttonText.textContent = available ? labelWithPrice : (strings.soldOut || 'Sold out');
        }
        if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
      });
    });
  };

  const initializeGalleries = () => {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      gallery.dataset.initialized = 'true';
      const main = gallery.querySelector('[data-product-main-image]');
      gallery.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gallery-thumb]');
        if (!button || !main) return;
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => thumb.classList.remove('is-active'));
        button.classList.add('is-active');
        main.src = button.dataset.image;
        main.srcset = '';
        main.alt = button.dataset.alt || '';
      });
    });
  };

  const initialize = () => {
    window.scantrackCart = window.scantrackCart || new CartDrawer();
    initializeProductForms();
    initializeGalleries();
  };

  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
})();
